// File: components/actions/FAB.js

import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';

/**
 * FAB (Floating Action Button) Component
 * 
 * Floating action button for quick access to search.
 * Mobile-first design with touch-friendly 56px size.
 * Auto-hides on scroll down, shows on scroll up.
 * 
 * @param {Object} props - Component props
 * @param {function} props.onClick - Handler for FAB click
 * @param {string} props.icon - Icon to display (default: search)
 * @param {string} props.label - ARIA label
 * @param {boolean} props.hideOnScroll - Auto-hide on scroll down
 * @param {number} props.scrollThreshold - Scroll threshold in pixels
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.position - Position (bottom-right, bottom-left, bottom-center)
 * @param {string} props.className - Additional CSS classes
 */
const FAB = ({
    onClick,
    label = 'Search',
    hideOnScroll = true,
    scrollThreshold = 100,
    disabled = false,
    position = 'bottom-right',
    className = ''
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    useEffect(() => {
        if (!hideOnScroll) {
            setIsVisible(true);
            return;
        }

        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // Show if scrolled to top
            if (currentScrollY < scrollThreshold) {
                setIsVisible(true);
                setLastScrollY(currentScrollY);
                return;
            }

            // Show on scroll up, hide on scroll down
            if (currentScrollY < lastScrollY) {
                setIsVisible(true);
            } else if (currentScrollY > lastScrollY) {
                setIsVisible(false);
            }

            setLastScrollY(currentScrollY);
        };

        // Throttle scroll events
        let ticking = false;
        const throttledHandleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledHandleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
        };
    }, [hideOnScroll, scrollThreshold, lastScrollY]);

    // Handle click
    const handleClick = () => {
        if (disabled) return;
        
        if (onClick) {
            onClick();
        }
    };

    return (
        <button
            className={`fab fab-${position} ${!isVisible ? 'hidden' : ''} ${disabled ? 'disabled' : ''} ${className}`}
            onClick={handleClick}
            disabled={disabled}
            aria-label={label}
            type="button"
        >
            <Search size={24} />
        </button>
    );
};

export default FAB;
